'use client';

// Composer dictation: the recording state inside the composer (operator UX
// round 1, U2; ChatGPT's dictation state, keeping our live text).
//
// - `DictationTranscript` sits in the composer's text area. Final text is in
//   the normal text colour; the partial is greyed until it is final. It is
//   read-only: nothing reaches the composer's text before ✓, and dictation
//   never sends (D31). `role="log"` announces finals only (the partial is
//   aria-hidden). Before the first word it shows the phase ("Listening…") as
//   greyed placeholder text.
// - `DictationRow` replaces the composer's tool row: the dimmed "+", a
//   waveform across the full width, then ✕ (cancel) and ✓ (finish: insert at
//   the caret), 44 px targets with ✓ the primary action. A countdown, a
//   reconnect, "Finishing…" or a backup engine shows as a small label above
//   the waveform. Focus moves to ✓ on start, so the keyboard commit is one key
//   away; Esc anywhere in the row cancels.
// - `SpeechLiveRegion` stays mounted for the composer's whole life and
//   announces "Inserted" and the first notice of each kind, never every
//   second of a countdown.
// There is no language control: engines detect the spoken language.
import { Button } from '@/components/ui/button';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import speechCopy from '@/i18n/ns/speech';
import { cn } from '@/lib/utils';
import { DictationWaveform } from '@/speech/dictation-waveform';
import { CheckIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, type FC, type ReactNode } from 'react';
import type { CaptionSegment, SpeechState } from '../interactive/store';
import { useSpeech, useSpeechState } from './speech-provider';

type Copy = Readonly<Record<keyof typeof speechCopy.en, string>>;
type StatusInput = Pick<SpeechState, 'phase' | 'notice' | 'degraded'>;

function noticeText(copy: Copy, notice: SpeechState['notice']): string | null {
  if (!notice) return null;
  const template = notice.kind === 'idle-stop' ? copy.idleStopping : copy.limitWarning;
  return formatCopy(template, { seconds: notice.seconds });
}

const ROW_PHASE_LABEL: Partial<Record<SpeechState['phase'], keyof Copy>> = {
  reconnecting: 'reconnecting',
  offline: 'reconnecting',
  committing: 'finishing',
};

/**
 * What the recording state says, from copy keys only. Exported for tests.
 *   placeholder: the greyed text before the first word;
 *   label: the small line above the waveform, only when something needs
 *     attention (a notice first, then a reconnect, finishing, a backup engine).
 */
export function dictationStatus(copy: Copy, state: StatusInput) {
  const placeholder = state.phase === 'starting' ? copy.starting : copy.listening;
  const phaseLabel = ROW_PHASE_LABEL[state.phase];
  const label =
    noticeText(copy, state.notice) ??
    (phaseLabel ? copy[phaseLabel] : state.degraded ? copy.degraded : '');
  return { placeholder, label };
}

const SegmentText: FC<{ segment: CaptionSegment }> = ({ segment }) =>
  segment.final ? (
    <span data-final="true">{segment.text} </span>
  ) : (
    <span aria-hidden="true" data-final="false" className="text-muted-foreground">
      {segment.text}{' '}
    </span>
  );

/** The live text in the composer's text area (see the header). */
export const DictationTranscript: FC<{ className?: string }> = ({ className }) => {
  const copy = useNamespaceCopy(speechCopy);
  const phase = useSpeechState((state) => state.phase);
  const segments = useSpeechState((state) => state.segments);
  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [segments]);
  const { placeholder } = dictationStatus(copy, { phase, notice: null, degraded: false });
  return (
    <div
      ref={logRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label={copy.captionLabel}
      dir="auto"
      data-slot="speech-transcript"
      className={cn(
        'text-foreground max-h-40 min-h-11 min-w-0 overflow-y-auto px-1.5 py-2.5 text-base leading-6 break-words whitespace-pre-wrap md:min-h-10 md:px-2.5 md:py-1 md:leading-normal',
        className,
      )}
    >
      {segments.map((segment) => (
        <SegmentText key={segment.key} segment={segment} />
      ))}
      {segments.length === 0 && (
        <span aria-hidden="true" className="text-muted-foreground">
          {placeholder}
        </span>
      )}
    </div>
  );
};

/** The recording row: dimmed "+", the waveform, ✕ and ✓ (see the header). */
export const DictationRow: FC<{ leading?: ReactNode }> = ({ leading }) => {
  const speech = useSpeech();
  const copy = useNamespaceCopy(speechCopy);
  const phase = useSpeechState((state) => state.phase);
  const notice = useSpeechState((state) => state.notice);
  const degraded = useSpeechState((state) => state.degraded);
  const level = useSpeechState((state) => state.level);
  const finishRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (phase === 'starting') finishRef.current?.focus({ preventScroll: true });
  }, [phase]);
  if (!speech) return null;
  const { label } = dictationStatus(copy, { phase, notice, degraded });
  const committing = phase === 'committing';
  return (
    <div
      data-slot="speech-dictation"
      data-phase={phase}
      className="col-span-full flex min-w-0 items-center gap-1 md:gap-1.5"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || event.nativeEvent.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        void speech.controller.cancel();
      }}
    >
      {leading && (
        <div inert className="flex shrink-0 items-center opacity-40">
          {leading}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-center px-1">
        {label && (
          <span
            data-slot="speech-status"
            className={cn(
              'truncate text-xs leading-4',
              notice ? 'text-foreground font-medium' : 'text-muted-foreground',
            )}
          >
            {label}
          </span>
        )}
        <DictationWaveform level={level} phase={phase} className={label ? 'h-6' : 'h-8'} />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-slot="speech-cancel"
        className="text-muted-foreground hover:text-foreground size-11 shrink-0 rounded-full"
        aria-label={copy.cancelDictation}
        title={copy.cancelDictation}
        onClick={() => void speech.controller.cancel()}
      >
        <XIcon className="size-5" aria-hidden="true" />
      </Button>
      <Button
        ref={finishRef}
        type="button"
        variant="default"
        size="icon"
        data-slot="speech-finish"
        className="size-11 shrink-0 rounded-full"
        aria-label={copy.finish}
        title={copy.finish}
        aria-disabled={committing || undefined}
        onClick={() => {
          if (!committing) void speech.controller.stop();
        }}
      >
        <CheckIcon className="size-5" aria-hidden="true" />
      </Button>
    </div>
  );
};

/**
 * The polite status region, mounted for the composer's whole life so that a
 * change is always announced: "Inserted" (re-keyed per insert) and the first
 * notice of each kind (not every second of a countdown).
 */
export const SpeechLiveRegion: FC = () => {
  const speech = useSpeech();
  const copy = useNamespaceCopy(speechCopy);
  const inserted = useSpeechState((state) => state.inserted);
  const notice = useSpeechState((state) => state.notice);
  const first = useRef<{ kind: string; text: string } | null>(null);
  if (!notice) first.current = null;
  else if (first.current?.kind !== notice.kind)
    first.current = { kind: notice.kind, text: noticeText(copy, notice) ?? '' };
  if (!speech) return null;
  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-slot="speech-announcer"
      className="sr-only"
    >
      {first.current ? (
        <span key={`notice-${first.current.kind}`}>{first.current.text}</span>
      ) : inserted > 0 ? (
        <span key={`inserted-${inserted}`}>{copy.inserted}</span>
      ) : null}
    </span>
  );
};
